import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaPhone,
  FaCheck,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { auth } from "./utils/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";

// 🔥 Import Firestore helper
import { ensureUserDoc } from "./utils/firestoreHelpers";

interface SignUpPageProps {
  onLoginSuccess: () => void;
  onSwitchToLogin?: () => void; // Optional prop for modal switching
}

const SignUpPage: React.FC<SignUpPageProps> = ({
  onLoginSuccess,
  onSwitchToLogin,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "+91",
    password: "",
    confirmPassword: "",
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<
    "weak" | "medium" | "strong" | ""
  >("");

  // Checkbox state for agreement
  const [agreed, setAgreed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    const { password } = formData;
    if (password.length === 0) setPasswordStrength("");
    else if (password.length < 8) setPasswordStrength("weak");
    else if (
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        password
      )
    )
      setPasswordStrength("strong");
    else setPasswordStrength("medium");
  }, [formData.password]);

  const validateForm = () => {
    const { fullName, email, mobile, password, confirmPassword } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\+91\d{10}$/;

    if (!fullName || fullName.length < 3) {
      setErrorMessage("Full name must be at least 3 characters long");
      return false;
    }

    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address");
      return false;
    }

    if (!mobileRegex.test(mobile)) {
      setErrorMessage("Mobile number must be in format: +91XXXXXXXXXX");
      return false;
    }

    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long");
      return false;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return false;
    }

    if (!agreed) {
      setErrorMessage("You must agree to the Terms of Service and Privacy Policy");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // 🔥 Sync Firestore user doc after signup
      await ensureUserDoc(userCredential.user);

      // Save user details
      localStorage.setItem(
        "user",
        JSON.stringify({
          uid: userCredential.user.uid,
          fullName: formData.fullName,
          email: formData.email,
          mobile: formData.mobile,
        })
      );

      setSubmitted(true);
      setTimeout(() => {
        onLoginSuccess();
        navigate("/main");
      }, 1000);
    } catch (error: any) {
      setErrorMessage(error.message || "Error creating account");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processed = value;

    if (name === "mobile") {
      // Remove any existing +91 prefix if user tries to edit
      processed = processed.replace(/^(\+91)?/, "");
      // Remove all non-digit characters
      processed = processed.replace(/\D/g, "");
      // Limit to max 10 digits
      if (processed.length > 10) {
        processed = processed.slice(0, 10);
      }
      // Prepend +91 prefix
      processed = "+91" + processed;
    }

    setFormData((prev) => ({ ...prev, [name]: processed }));
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-500 to-purple-600 overflow-hidden px-4">
      <motion.div
        className="bg-white p-10 rounded-2xl shadow-2xl w-full max-w-md text-center z-10"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="text-3xl font-extrabold text-gray-800 mb-2">
          THE KARIGAR STOP
        </h1>
        <h2 className="text-xl font-semibold text-gray-600 mb-6">
          Create Account
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <FaUser className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
              required
            />
          </div>

          <div className="relative">
            <FaEnvelope className="absolute left-3 top-3 text-gray-500" />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
              required
            />
          </div>

          <div className="relative">
            <FaPhone className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              name="mobile"
              placeholder="Mobile Number (+91)"
              value={formData.mobile}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
              maxLength={13}
              required
            />
          </div>

          <div className="relative">
            <FaLock className="absolute left-3 top-3 text-gray-500" />
            <input
              type={passwordVisible ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible(!passwordVisible)}
              className="absolute right-3 top-3 text-gray-500"
              tabIndex={-1}
            >
              {passwordVisible ? <FaEyeSlash /> : <FaEye />}
            </button>

            {passwordStrength && (
              <div className="mt-1 text-sm">
                Password strength:{" "}
                <span
                  className={`font-semibold ${
                    passwordStrength === "weak"
                      ? "text-red-500"
                      : passwordStrength === "medium"
                      ? "text-yellow-500"
                      : "text-green-500"
                  }`}
                >
                  {passwordStrength}
                </span>
              </div>
            )}
          </div>

          <div className="relative">
            <FaLock className="absolute left-3 top-3 text-gray-500" />
            <input
              type={confirmPasswordVisible ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 shadow-md ${
                formData.confirmPassword && formData.password !== formData.confirmPassword
                  ? "border-red-500"
                  : ""
              }`}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
              className="absolute right-3 top-3 text-gray-500"
              tabIndex={-1}
            >
              {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Terms and Privacy Policy Checkbox */}
          <div className="flex items-center text-sm text-gray-600 mb-3">
            <input
              type="checkbox"
              id="terms"
              checked={agreed}
              onChange={() => setAgreed(!agreed)}
              className="mr-2 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
              required
            />
            <label htmlFor="terms">
              I agree to the{" "}
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition duration-300"
          >
            Sign Up
          </motion.button>
        </form>

        <p className="text-gray-600 mt-4">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => {
              if (onSwitchToLogin) onSwitchToLogin();
              else navigate("/login");
            }}
            className="text-blue-600 hover:underline"
          >
            Login
          </button>
        </p>

        {/* Error message positioned just below the login link */}
        <div className="mt-2 min-h-[24px]">
          <AnimatePresence>
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="text-red-500 text-sm"
              >
                {errorMessage}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {submitted && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute bg-green-500 text-white p-4 rounded-full"
        >
          <FaCheck size={32} />
        </motion.div>
      )}
    </div>
  );
};

export default SignUpPage;
